import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Typography, Modal, Form, Input, Select, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, LinkOutlined, CopyOutlined } from '@ant-design/icons';
import { User } from '@packet/management/src/DTO/user';
import { getUserList, createUser, updateUser, deleteUser } from '../../api/user';
import { getRoleList } from '../../api/role';
import { getOrganizationList } from '../../api/organization';
import { createInvite } from '@packet/management/src/api/auth';
import type { CreateUserRequest, UpdateUserRequest, GetUserListRequest } from '../../DTO/user';
import type { Role } from '../../DTO/role';
import type { Organization } from '../../DTO/organization';
import { useUserDetailModalStore } from '@/stores/userDetailModal';

const { Title } = Typography;
const { Option } = Select;

const roleColors = {
  admin: 'red',
  manager: 'blue',
  auditor: 'green',
  user: 'default',
};

const AccountManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const { openUserDetail } = useUserDetailModalStore();

  // Invite modal state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string>('');
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string>('');
  const [inviteLoading, setInviteLoading] = useState(false);

  // 获取用户列表
  const fetchUserList = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const params: GetUserListRequest = {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        order: 'desc',
      };

      const response = await getUserList(params);
      console.log('获取用户列表:', response);
      if (response.code === 0) {
        setUsers(response.data.data);
        setPagination((prev) => ({
          ...prev,
          pageSize: pageSize,
          current: page,
          total: response.data.total,
        }));
      } else {
        message.error(response.message || '获取用户列表失败');
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取角色列表
  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await getRoleList({
        offset: 0,
        limit: 1000, // 获取所有角色
        order: 'desc',
      });

      if (response.code === 0) {
        // 过滤掉禁用状态的角色 (status === 1 为禁用)
        // const activeRoles = response.data.data.filter(
        //   (role) => role.status === 0
        // );
        // setRoles(activeRoles);
        setRoles(response.data.data);
      } else {
        message.error(response.message || '获取角色列表失败');
      }
    } catch (error) {
      console.error('获取角色列表失败:', error);
      message.error('获取角色列表失败');
    } finally {
      setRolesLoading(false);
    }
  };

  // 获取组织列表
  const fetchOrganizations = async () => {
    try {
      setOrganizationsLoading(true);
      const response = await getOrganizationList({
        offset: 0,
        limit: 1000, // 获取所有组织
        order: 'desc',
      });

      if (response.code === 0) {
        // 过滤掉禁用状态的组织 (status === 1 为禁用)
        const activeOrganizations = response.data.data.filter((org) => org.status != 1);
        setOrganizations(activeOrganizations);
      } else {
        message.error(response.message || '获取组织列表失败');
      }
    } catch (error) {
      console.error('获取组织列表失败:', error);
      message.error('获取组织列表失败');
    } finally {
      setOrganizationsLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchUserList();
    fetchRoles();
    fetchOrganizations();
  }, []);

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    fetchUserList(pagination.current, pagination.pageSize);
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'username',
      key: 'username',
      sorter: (a: User, b: User) => a.username.localeCompare(b.username),
    },
    {
      title: '所属组织/机构',
      dataIndex: ['organization', 'organization_name'],
      key: 'organization.organization_name',
      render: (text: string, record: User) => {
        return record.organization?.organization_name || '-';
      },
      sorter: (a: User, b: User) => {
        const aOrgName = a.organization?.organization_name || '';
        const bOrgName = b.organization?.organization_name || '';
        return aOrgName.localeCompare(bOrgName);
      },
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '联系方式',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '角色',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: User['roles']) => {
        if (!roles || roles.length === 0) return '-';
        return roles.map((role, index) => (
          <Tag key={role.id} color={roleColors[role.role_name as keyof typeof roleColors] || 'default'}>
            {role.role_name}
          </Tag>
        ));
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => <Tag color={status === 0 ? 'green' : 'red'}>{status === 0 ? '激活' : '停用'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 200,
      render: (_: number, record: User) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除这个用户吗？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    // 重新获取组织列表以确保数据最新
    fetchOrganizations();
    setIsModalOpen(true);
  };

  // Generate invite link
  const handleGenerateInvite = async () => {
    try {
      setInviteLoading(true);
      const response = await createInvite({ expire_hours: 24 });
      if (response.code === 0 && response.data) {
        setInviteUrl(response.data.invite_url);
        setInviteExpiresAt(response.data.expires_at);
        setIsInviteModalOpen(true);
      } else {
        message.error(response.message || 'Failed to generate invite link');
      }
    } catch (error) {
      console.error('Failed to generate invite:', error);
      message.error('Failed to generate invite link');
    } finally {
      setInviteLoading(false);
    }
  };

  // Copy invite URL to clipboard
  const handleCopyInviteUrl = () => {
    navigator.clipboard.writeText(inviteUrl).then(() => {
      message.success('Invite link copied to clipboard');
    }).catch(() => {
      message.error('Failed to copy');
    });
  };

  const handleViewDetail = async (user: User) => {
    try {
      openUserDetail(user.id);
    } catch (error) {
      console.error('获取用户详情失败:', error);
      message.error('获取用户详情失败');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    // 重新获取组织列表以确保数据最新
    fetchOrganizations();
    // 设置表单值
    const formValues = {
      username: user.username,
      email: user.email,
      phone: user.phone,
      role_ids: user.roles?.map((role) => role.id) || [],
      organization_id: user.organization_id,
      status: user.status,
      describe: user.describe,
    };
    form.setFieldsValue(formValues);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await deleteUser({ id });
      if (response.code === 0) {
        message.success('用户删除成功');
        // 重新获取用户列表
        fetchUserList(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || '删除用户失败');
      }
    } catch (error) {
      console.error('删除用户失败:', error);
      message.error('删除用户失败');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      const values = await form.validateFields();

      if (editingUser) {
        // 编辑用户
        const updateData: UpdateUserRequest = {
          id: editingUser.id,
          username: values.username,
          // email: values.email,
          phone: values.phone,
          role_ids: values.role_ids,
          organization_id: values.organization_id,
          status: values.status,
          describe: values.describe,
        };

        const response = await updateUser(updateData);
        if (response.code === 0) {
          message.success('用户信息更新成功');
          setIsModalOpen(false);
          form.resetFields();
          // 重新获取用户列表
          fetchUserList(pagination.current, pagination.pageSize);
        } else {
          message.error(response.message || '更新用户失败');
        }
      } else {
        // 新增用户
        const createData: CreateUserRequest = {
          username: values.username,
          email: values.email,
          phone: values.phone,
          role_ids: values.role_ids,
          organization_id: values.organization_id,
          describe: values.describe,
          url: `${import.meta.env.VITE_AUDIT_SITE_URL}/set-password`, // 使用当前网站的基础URL
        };

        const response = await createUser(createData);
        if (response.code === 0) {
          message.success('用户创建成功');
          setIsModalOpen(false);
          form.resetFields();
          // 重新获取用户列表
          fetchUserList(pagination.current, pagination.pageSize);
        } else {
          message.error(response.message || '创建用户失败');
        }
      }
    } catch (error) {
      console.error('表单验证失败:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={2}>账号管理</Title>
        <Space>
          <Button type="default" icon={<LinkOutlined />} onClick={handleGenerateInvite} loading={inviteLoading}>
            生成邀请链接
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            创建用户
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        onChange={handleTableChange}
      />

      <Modal
        title={editingUser ? '编辑用户' : '创建用户'}
        okText={editingUser ? '保存' : '创建并发送邮件'}
        open={isModalOpen}
        confirmLoading={submitLoading}
        onOk={handleSubmit}
        onCancel={() => setIsModalOpen(false)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { type: 'email', message: '请输入有效的邮箱地址' },
              { required: true, message: '请输入邮箱' },
            ]}
          >
            <Input placeholder="请输入邮箱地址" disabled={!!editingUser} />
          </Form.Item>

          <Form.Item name="username" label="姓名" rules={[{ min: 2, message: '姓名至少2个字符' }]}>
            <Input placeholder="请输入姓名" />
          </Form.Item>

          <Form.Item name="phone" label="电话">
            <Input placeholder="请输入电话号码" />
          </Form.Item>

          <Form.Item name="organization_id" label="所属机构" rules={[{ required: true, message: '请选择所属机构' }]}>
            <Select placeholder="请选择所属机构" loading={organizationsLoading} notFoundContent={organizationsLoading ? '加载中...' : '暂无可用机构'}>
              {organizations.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.organization_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="role_ids" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
            <Select mode="multiple" placeholder="请选择角色" loading={rolesLoading} notFoundContent={rolesLoading ? '加载中...' : '暂无可用角色'}>
              {roles.map((role) => (
                <Option key={role.id} value={role.id}>
                  {role.role_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
            <Select placeholder="请选择状态">
              <Option value={0}>激活</Option>
              <Option value={1}>停用</Option>
            </Select>
          </Form.Item>

          <Form.Item name="describe" label="描述">
            <Input.TextArea placeholder="请输入描述" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Invite Link Modal */}
      <Modal
        title="邀请链接已生成"
        open={isInviteModalOpen}
        onCancel={() => setIsInviteModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsInviteModalOpen(false)}>
            关闭
          </Button>,
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={handleCopyInviteUrl}>
            复制链接
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">
            将此链接发送给新用户，他们可以使用此链接注册账号。
          </Typography.Text>
        </div>
        <Input.TextArea
          value={inviteUrl}
          readOnly
          autoSize={{ minRows: 2, maxRows: 4 }}
          style={{ marginBottom: 16 }}
        />
        <Typography.Text type="secondary">
          有效期至: {inviteExpiresAt ? new Date(inviteExpiresAt).toLocaleString() : '-'}
        </Typography.Text>
      </Modal>
    </div>
  );
};

export default AccountManagement;
